// frontend/src/appRoutes.js
//
// THE route table, and the route preload src/index.js waits for before it
// hydrates a prerendered snapshot (#464). Split out of App.js so App.js stays
// a component-only module (a clean Fast Refresh boundary) and so index.js and
// routePreload.test.js read the routes without importing the provider stack.
// App.js renders `appRoutes` inside its route-level <Suspense>, followed by
// ReleaseAuthBootOnPublicRoute (bottom of this file, beside the isProtectedPath
// it reads); nothing here imports App.js (no cycle).

import React, { lazy, Suspense, useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
} from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import RootRoute from "./components/RootRoute"; // "/" — Homepage, or a redirect for a signed-in visitor
import AuthLayout from "./components/AuthLayout";
import PageLoadingFallback from "./components/PageLoadingFallback";
// /settings, /admin and /admin/users/:userId load their page INSIDE AuthLayout,
// under a breathing skeleton of the page body, so the real navbar and footer
// render at once (components/loading/PageBodySuspense.js). The boundary is
// eager: it is the fallback FOR those chunks. The two skeletons are code-split
// (LazySettingsPageSkeleton.js, LazyAdminPageSkeleton.js) - they serve
// signed-in routes only and must not ride in every visitor's main chunk;
// ProtectedRoute warms them once it knows who is signed in. On the two admin
// routes AwaitPageSkeleton holds the branded loader until that skeleton is
// in, so a cold boot never shows the navbar over an empty body.
import PageBodySuspense from "./components/loading/PageBodySuspense";
import LazySettingsPageSkeleton from "./components/loading/LazySettingsPageSkeleton";
import LazyAdminPageSkeleton from "./components/loading/LazyAdminPageSkeleton";
import AwaitPageSkeleton from "./components/loading/AwaitPageSkeleton";
// /payment/success and /payment/failure load INSIDE AuthLayout too, under the
// payment card's own skeleton. Eager, unlike the two above: HYP returns the
// browser to these routes with a FULL page load, so a code-split skeleton
// would always be cold (see the module's header).
import PaymentPageSkeleton from "./components/payment/PaymentPageSkeleton";
// The lazy Login page lives in its own module because ProtectedRoute warms the
// SAME object before redirecting to /login, and ProtectedRoute cannot import
// this file (this file imports it). Prerendered: a lazyWithPreload.
import Login from "./components/LazyLoginPage";
// Old /admin/interventions + /admin/quota-management URLs -> the dashboard's
// Interventions tab. Eager: it is a two-line redirect with no page code.
import {
  LegacyAdminRedirect,
  PROFESSIONS_TAB_INDEX,
} from "./pages/admin/interventions/legacyRedirects";
// The profession landing pages' generic routed component and THE list of them.
// Both are eager and both are tiny: the registry is slugs and routes only, and
// ProfessionPage holds no content and no page shell (see its header).
import ProfessionPage from "./pages/professions/ProfessionPage";
import professionRegistry from "./content/professions/registry";
import lazyWithPreload from "./utils/lazyWithPreload";
import { isAuthBootActive, releaseAuthBoot } from "./utils/authBoot";

// ---------------------------------------------------------------------------
// Route-level code splitting
//
// Every route component except the public Homepage is loaded on demand via
// React.lazy(). An anonymous landing-page visitor therefore never downloads the
// authenticated app (Dashboard, Analytics, BI, Payments, Admin, …) — each lazy
// import becomes its own webpack chunk fetched only when the route is visited.
// A single <Suspense> boundary around these routes (in App.js) renders
// PageLoadingFallback while a chunk loads.
//
// PRERENDERED ROUTES USE lazyWithPreload, NOT lazy (#464). Their snapshots are
// HYDRATED, and a hydrating route must not suspend: a route boundary left
// dehydrated while its chunk downloads is dropped by React 19 as soon as any
// context above it updates, and PageLoadingFallback is committed over the
// page (utils/lazyWithPreload.js). src/index.js calls preloadRouteForPath()
// (bottom of this file) and hydrates once the page module is in, so the first
// render finds it loaded. The route table below is the ONLY list it reads -
// src/__tests__/routePreload.test.js fails if a prerendered route's page is not
// preloadable. The profession pages get the same through ProfessionPage.preload.
// ---------------------------------------------------------------------------

// Public / auth pages. lazyWithPreload = prerendered (see the header above).
// (Login is imported at the top of the file - components/LazyLoginPage.js.)
const Register = lazyWithPreload(() => import("./pages/Register"));
const ForgotPassword = lazyWithPreload(() => import("./pages/ForgotPassword")); // Task 9.5A (6-digit code flow)
const EmailVerification = lazy(() => import("./pages/EmailVerification"));
const Unsubscribe = lazy(() => import("./pages/unsubscribe/Unsubscribe"));
// Supporter network — PUBLIC, reached only from an emailed link carrying a
// ?token=. No account, no session: the token is the whole authorization. Both
// render their own standalone RTL chrome (no AuthLayout), and both send robots
// noindex,nofollow.
const SupporterConfirm = lazy(() => import("./pages/supporter/SupporterConfirm"));
const SupporterUnsubscribe = lazy(() =>
  import("./pages/supporter/SupporterUnsubscribe")
);
// Enterprise payment link (#349) - PUBLIC, reached only from an emailed link
// whose token is in the PATH (moved out of the address on arrival - see
// EnterprisePay.js). No account, no session, no AuthLayout; both pages
// send robots noindex,nofollow through <Seo>. The result page is where HYP
// returns the customer, and carries no token.
const EnterprisePay = lazy(() => import("./pages/enterprisePay/EnterprisePay"));
const EnterprisePayResult = lazy(() =>
  import("./pages/enterprisePay/EnterprisePayResult")
);
const TermsOfUse = lazyWithPreload(() => import("./pages/legal/TermsOfUse"));
const PrivacyPolicy = lazyWithPreload(() => import("./pages/legal/PrivacyPolicy"));
const CookiePolicy = lazyWithPreload(() => import("./pages/legal/CookiePolicy"));
const AccessibilityStatement = lazyWithPreload(() => import("./pages/AccessibilityStatement"));
const Blog = lazyWithPreload(() => import("./pages/Blog"));
const BlogArticle = lazyWithPreload(() => import("./pages/BlogArticle"));
const BusinessWebsiteLanding = lazyWithPreload(() => import("./pages/BusinessWebsiteLanding"));
const FacebookGroupScanningLanding = lazyWithPreload(() => import("./pages/FacebookGroupScanningLanding"));
const SupporterNetworkLanding = lazyWithPreload(() => import("./pages/SupporterNetworkLanding"));
// Profession landing pages: ONE generic page (pages/ProfessionLanding.js), one
// generic routed component (pages/professions/ProfessionPage.js) and ONE list
// (content/professions/registry.js). Adding a profession is the content module
// plus one registry line - there is no file to add here and no route to write.
//
// ProfessionPage is imported EAGERLY and is a handful of lines: it holds no
// content and no page shell, and fetches both in parallel inside a single
// `lazy()` when its route is actually visited. A content module imported here
// would ride in the main bundle, and a typo in it would blank the whole SPA
// rather than one route.
//
// The routes are ENUMERATED from the registry, not a `/:slug` param: a param
// route would match every unknown path under the root and answer 200 with an
// empty page for it, and seoContentSync.test.js could not assert that route,
// prerender ROUTES and sitemap <loc> agree.
// (Both imported at the top of the file - `import/first`.)
const AccessibilityTest = lazy(() => import("./pages/AccessibilityTest"));
const SimpleAccessibilityTest = lazy(() => import("./pages/SimpleAccessibilityTest"));

// Authenticated product pages
//
// Dashboard is lazyWithPreload although it is never prerendered: it is where
// an authenticated boot of "/" lands, and preloadAuthBootLanding() (bottom of
// this file) starts its chunk at boot, in parallel with the session check,
// instead of only after it. findPreload() never looks inside a ProtectedRoute,
// so this does NOT make index.js wait for it before hydrating anything.
const Dashboard = lazyWithPreload(() => import("./pages/Dashboard"));
const AnalyticsDashboard = lazy(() => import("./pages/AnalyticsDashboard"));
const Profile = lazy(() => import("./pages/Profile")); // route: /personal-area
const VerifiedInterventions = lazy(() => import("./pages/VerifiedInterventions"));
const BusinessIntelligence = lazy(() => import("./pages/BusinessIntelligence"));
const WebsiteBuilder = lazy(() => import("./pages/WebsiteBuilder"));
const Payment = lazy(() => import("./pages/Payment"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentFailure = lazy(() => import("./pages/PaymentFailure"));
const Contact = lazy(() => import("./pages/Contact"));

// Large admin pages
// AdminDashboard and UserDetails carry a preload so AwaitPageSkeleton can start
// them beside the admin skeleton's chunk (never after it).
const AdminDashboard = lazyWithPreload(() => import("./pages/AdminDashboard")); // 3,502 LOC!
const RecommenderDashboard = lazy(() => import("./pages/RecommenderDashboard"));
const RecommenderEarnings = lazy(() => import("./pages/recommender/RecommenderEarnings"));
const UserDetails = lazyWithPreload(() => import("./pages/UserDetails"));
// The printable monthly client breakdown (recommender earnings PR-8d) - a
// document page: admin-only, no Navbar/AuthLayout.
const ClientStatementPrint = lazy(() =>
  import("./pages/admin/interventions/clientBilling/ClientStatementPrint")
);
const Settings = lazy(() => import("./pages/settings/Settings"));

// The route table, at module level so preloadRouteForPath() below reads the
// SAME elements <App> renders - react-router's own matcher over them, never a
// second list of paths that could drift from this one.
const appRoutes = (
  <Routes>
    {/* Public routes - without layout */}
    <Route path="/login" element={<Login />} />
    <Route path="/register" element={<Register />} />
    <Route path="/verify-email" element={<EmailVerification />} />
    {/* Password Reset Route (Task 9.5A - 6-digit code flow) */}
    <Route path="/forgot-password" element={<ForgotPassword />} />
    <Route path="/unsubscribe" element={<Unsubscribe />} />
    {/* Supporter network — public, token-bound. Deliberately NOT
        wrapped in AuthLayout: the people who land here have no account
        and app chrome would imply a relationship they do not have. */}
    <Route path="/supporter/confirm" element={<SupporterConfirm />} />
    <Route
      path="/supporter/unsubscribe"
      element={<SupporterUnsubscribe />}
    />
    {/* Enterprise payment link (#349) - public, token-bound. The
        static `result` route is declared BEFORE `:token` so the
        return page can never be read as a token. */}
    <Route path="/enterprise/pay/result" element={<EnterprisePayResult />} />
    {/* The emailed link carries the token in the path; the page moves
        it out of the address at once (#349 R3-03) and lives on at the
        clean `/enterprise/pay`, reading it from this tab's storage. */}
    <Route path="/enterprise/pay" element={<EnterprisePay />} />
    <Route path="/enterprise/pay/:token" element={<EnterprisePay />} />
    {/* Legal Pages - Public */}
    <Route path="/terms" element={<TermsOfUse />} />
    <Route path="/privacy" element={<PrivacyPolicy />} />
    <Route path="/cookies" element={<CookiePolicy />} />
    {/* Blog - Public (each page renders its own public AuthLayout) */}
    <Route path="/blog" element={<Blog />} />
    <Route path="/blog/:slug" element={<BlogArticle />} />
    {/* Business-website feature landing - Public (renders its own AuthLayout) */}
    <Route path="/business-website" element={<BusinessWebsiteLanding />} />
    {/* Facebook group-scanning SEO landing - Public (renders its own AuthLayout) */}
    <Route path="/facebook-group-scanning" element={<FacebookGroupScanningLanding />} />
    {/* Supporter-network SEO landing - Public (renders its own AuthLayout) */}
    <Route path="/supporter-network" element={<SupporterNetworkLanding />} />
    {/* Profession SEO landings - Public (the page renders its own
        AuthLayout). One <Route> per registry entry, generated: there
        is nothing per profession to write here. */}
    {professionRegistry.PROFESSION_PAGES.map(({ slug, route }) => (
      <Route key={route} path={route} element={<ProfessionPage slug={slug} />} />
    ))}
    <Route
      path="/accessibility"
      element={
        <AuthLayout>
          <AccessibilityStatement />
        </AuthLayout>
      }
    />

    {/* Protected routes - with layout */}
    <Route
      path="/dashboard"
      element={
        <ProtectedRoute allowedTypes={['trial', 'solo', 'professional', 'enterprise']}>
          <AuthLayout>
            <Dashboard />
          </AuthLayout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/analytics"
      element={
        <ProtectedRoute allowedTypes={['trial', 'solo', 'professional', 'enterprise', 'admin']}>
          <AuthLayout>
            <AnalyticsDashboard />
          </AuthLayout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/personal-area"
      element={
        <ProtectedRoute allowedTypes={['trial', 'solo', 'professional', 'enterprise', 'admin']}>
          <AuthLayout>
            <Profile />
          </AuthLayout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/profile"
      element={<Navigate to="/personal-area" replace />}
    />
    <Route
      path="/settings"
      element={
        <ProtectedRoute allowedTypes={['trial', 'solo', 'professional', 'enterprise', 'admin']}>
          <AuthLayout>
            <PageBodySuspense fallback={<LazySettingsPageSkeleton />}>
              <Settings />
            </PageBodySuspense>
          </AuthLayout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/interventions/verified"
      element={
        <ProtectedRoute requireInterventionEnabled>
          <AuthLayout>
            <VerifiedInterventions />
          </AuthLayout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/business-intelligence"
      element={
        <ProtectedRoute requireBIEnabled>
          <AuthLayout>
            <BusinessIntelligence />
          </AuthLayout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/website"
      element={
        // Business-website builder — gated on the derived
        // `website_eligible` flag (Professional/Enterprise + trialing
        // users), mirroring the server-side get_current_website_user
        // gate. Admin bypasses inside ProtectedRoute.
        <ProtectedRoute requireFlag="website_eligible">
          <AuthLayout>
            <WebsiteBuilder />
          </AuthLayout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/payment"
      element={
        <ProtectedRoute>
          <AuthLayout>
            <Payment />
          </AuthLayout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/payment/success"
      element={
        <ProtectedRoute>
          <AuthLayout>
            <PageBodySuspense fallback={<PaymentPageSkeleton />}>
              <PaymentSuccess />
            </PageBodySuspense>
          </AuthLayout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/payment/failure"
      element={
        <ProtectedRoute>
          <AuthLayout>
            <PageBodySuspense fallback={<PaymentPageSkeleton variant="notice" />}>
              <PaymentFailure />
            </PageBodySuspense>
          </AuthLayout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/contact"
      element={
        <ProtectedRoute>
          <AuthLayout>
            <Contact />
          </AuthLayout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/admin"
      element={
        <ProtectedRoute requireAdmin>
          <AwaitPageSkeleton skeleton={LazyAdminPageSkeleton} preloadPage={AdminDashboard.preload}>
            <AuthLayout>
              <PageBodySuspense fallback={<LazyAdminPageSkeleton />}>
                <AdminDashboard />
              </PageBodySuspense>
            </AuthLayout>
          </AwaitPageSkeleton>
        </ProtectedRoute>
      }
    />
    {/* The two former standalone pages live in the admin dashboard's
        Interventions tab now; their old URLs redirect (replace) so
        bookmarks and already-sent admin emails/pushes keep working.
        The target /admin route carries the admin gate. */}
    <Route
      path="/admin/interventions"
      element={<LegacyAdminRedirect />}
    />
    <Route
      path="/admin/quota-management"
      element={<LegacyAdminRedirect sub="quotas" />}
    />
    {/* Never pages: admin emails sent before their links were
        corrected point here. Both open the Professions tab, which
        hosts the profession review queue. */}
    <Route
      path="/admin/review-queue"
      element={<LegacyAdminRedirect tab={PROFESSIONS_TAB_INDEX} />}
    />
    <Route
      path="/admin/professions"
      element={<LegacyAdminRedirect tab={PROFESSIONS_TAB_INDEX} />}
    />
    <Route
      path="/admin/users/:userId"
      element={
        <ProtectedRoute requireAdmin>
          <AwaitPageSkeleton skeleton={LazyAdminPageSkeleton} preloadPage={UserDetails.preload}>
            <AuthLayout>
              <PageBodySuspense fallback={<LazyAdminPageSkeleton variant="userDetails" />}>
                <UserDetails />
              </PageBodySuspense>
            </AuthLayout>
          </AwaitPageSkeleton>
        </ProtectedRoute>
      }
    />
    {/* No <Suspense> of its own: while the chunk loads, ProtectedRoute's
        boot boundary shows the branded loader on a cold boot only (so the
        boot splash never fades onto a spinner). An in-app navigation keeps
        the previous page on screen until the chunk is in - a transition into
        a boundary that is already revealed (App's, or the reused boot one). */}
    <Route
      path="/admin/client-statements/:id/print"
      element={
        <ProtectedRoute requireAdmin>
          <ClientStatementPrint />
        </ProtectedRoute>
      }
    />
    <Route
      path="/recommender-dashboard"
      element={
        <Suspense fallback={<PageLoadingFallback />}>
          {/* Gated by the recommender capability (active Recommender
              profile), not by user_type — supports dual-role accounts. */}
          <ProtectedRoute requireRecommender>
            <RecommenderDashboard />
          </ProtectedRoute>
        </Suspense>
      }
    />
    <Route
      path="/recommender/earnings"
      element={
        <Suspense fallback={<PageLoadingFallback />}>
          {/* Same capability gate as the dashboard. An admin who is
              not a recommender passes ProtectedRoute; the page itself
              then shows its "for recommenders" card instead of
              calling an API that would 403. */}
          <ProtectedRoute requireRecommender>
            <RecommenderEarnings />
          </ProtectedRoute>
        </Suspense>
      }
    />
    <Route
      path="/accessibility-test"
      element={
        <ProtectedRoute>
          <AuthLayout>
            <AccessibilityTest />
          </AuthLayout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/simple-accessibility-test"
      element={
        <ProtectedRoute>
          <AuthLayout>
            <SimpleAccessibilityTest />
          </AuthLayout>
        </ProtectedRoute>
      }
    />

    {/* Homepage — RootRoute renders it for anonymous visitors and
        redirects a signed-in one to /dashboard before it can mount. */}
    <Route path="/" element={<RootRoute />} />
  </Routes>
);

export default appRoutes;

// ---------------------------------------------------------------------------
// preloadRouteForPath (#464) - the page module of the route `pathname` renders,
// started now, so src/index.js can hydrate a prerendered snapshot only once the
// route can render without suspending (see the header of this file).
// ---------------------------------------------------------------------------

/** The element tree is walked at most this deep looking for a preloadable page. */
const PRELOAD_SEARCH_DEPTH = 4;

/**
 * The preload of the first component in `element`'s tree that has one: the
 * route element itself (`<Login />`), or a page inside a wrapper
 * (`<AuthLayout><AccessibilityStatement /></AuthLayout>`). A component's
 * `preload(props)` receives the element's props - ProfessionPage needs its slug.
 *
 * Never inside a ProtectedRoute - DEFENSIVE. A protected page is never
 * prerendered, so no snapshot's hydration can be waiting for it. index.js can
 * still ask for one: a protected address is not a file on the bucket, so it
 * boots through 404.html, which lands on "/" (the homepage snapshot), and the
 * inline script at the end of <body> restores the address BEFORE the deferred
 * bundle runs - so index.js sees /settings over a homepage snapshot (as it does
 * when the service worker's offline app shell is served at a protected
 * address). The snapshot on screen is then not that page: waiting for the
 * page's chunk would only delay hydrating it, or trip index.js's failed-chunk
 * reload. (Dashboard has a preload for the authenticated boot alone -
 * preloadAuthBootLanding.)
 *
 * @returns {(() => Promise<unknown>) | null}
 */
function findPreload(element, depth = 0) {
  if (!React.isValidElement(element) || depth > PRELOAD_SEARCH_DEPTH) return null;
  const { type, props } = element;
  if (type === ProtectedRoute) return null;
  if (type && typeof type.preload === "function") return () => type.preload(props);
  let found = null;
  React.Children.forEach(props && props.children, (child) => {
    if (!found) found = findPreload(child, depth + 1);
  });
  return found;
}

let routeObjects = null;

/**
 * The element of the route react-router would render at `pathname` - matched
 * with its own matchRoutes over the very table <App> renders (so ranking,
 * params and a trailing slash behave exactly as in <Routes>) - or null for an
 * unknown path.
 *
 * @param {string} pathname
 * @returns {React.ReactElement | null}
 */
function routeElementForPath(pathname) {
  if (!routeObjects) routeObjects = createRoutesFromChildren(appRoutes.props.children);
  const matches = matchRoutes(routeObjects, pathname);
  if (!matches || matches.length === 0) return null;
  return matches[matches.length - 1].route.element;
}

/**
 * The preload for the route react-router would render at `pathname`
 * (routeElementForPath), or null when that route has no preloadable page: "/"
 * (Homepage is eager), the authenticated app, an unknown path.
 *
 * @param {string} pathname
 * @returns {(() => Promise<unknown>) | null}
 */
export function routePreloadForPath(pathname) {
  return findPreload(routeElementForPath(pathname));
}

/** True when `element`'s tree holds a ProtectedRoute, as deep as findPreload looks. */
function containsProtectedRoute(element, depth = 0) {
  if (!React.isValidElement(element) || depth > PRELOAD_SEARCH_DEPTH) return false;
  if (element.type === ProtectedRoute) return true;
  let found = false;
  React.Children.forEach(element.props && element.props.children, (child) => {
    if (!found) found = containsProtectedRoute(child, depth + 1);
  });
  return found;
}

/**
 * Does the route react-router would render at `pathname` sit behind a
 * ProtectedRoute - directly (`<ProtectedRoute>...`) or inside a wrapper
 * (`<Suspense><ProtectedRoute>...`)? Matched exactly as routePreloadForPath
 * matches, over the same table. An unknown path, "/" and every public page are
 * not protected; so is a redirect route (/profile, the legacy /admin/...
 * addresses) - its TARGET is what is protected.
 *
 * Used by App's ReleaseAuthBootOnPublicRoute (utils/authBoot.js): a protected
 * page takes the boot splash down itself, a public one without AuthLayout
 * would otherwise leave it up until App's 3s cap. Pure; never throws (anything
 * unreadable reads as "not protected", which only means the splash comes down).
 *
 * @param {string} pathname
 * @returns {boolean}
 */
export function isProtectedPath(pathname) {
  try {
    if (typeof pathname !== "string") return false;
    return containsProtectedRoute(routeElementForPath(pathname));
  } catch (e) {
    return false;
  }
}

/**
 * Start loading the page module for `pathname`. Returns its promise, or null
 * when there is nothing to wait for. Never throws (a preload that throws
 * synchronously reads as null). What src/index.js does with each outcome -
 * hydrate at once, hydrate once loaded or timed out, reload once on a
 * rejection - is documented there.
 *
 * @param {string} pathname
 * @returns {Promise<unknown> | null}
 */
export function preloadRouteForPath(pathname) {
  try {
    const preload = routePreloadForPath(pathname);
    return preload ? Promise.resolve(preload()) : null;
  } catch (e) {
    return null;
  }
}

/** The browser's own pathname, or null where it cannot be read. */
const browserPathname = () => {
  try {
    return typeof window !== "undefined" && window.location ? window.location.pathname : null;
  } catch (e) {
    return null;
  }
};

/**
 * Takes the authenticated-boot splash down once a PUBLIC page has committed
 * (backlog #158; utils/authBoot.js lists every release site). The splash goes
 * up on any signed-in boot of "/" - and every 404.html deep link boots "/" -
 * but only AuthLayout and ProtectedRoute release it, so a public page without
 * AuthLayout (/unsubscribe from an email, /enterprise/pay/..., /verify-email,
 * /supporter/..., an unknown address) would sit under it until App's 3s cap.
 *
 * App renders this as the LAST child of its route <Suspense>, after the
 * routes: it commits together with the page, so a lazy public page releases
 * once its chunk is in and the page has committed - never onto
 * PageLoadingFallback - and an in-app navigation (a transition) commits it
 * only with the new page.
 *
 * It stays put - the page releases the splash itself - on:
 *   - "/": RootRoute is about to send a hinted visitor to /dashboard;
 *   - a protected route (isProtectedPath): ProtectedRoute's boundary or
 *     AuthLayout releases once the page has painted;
 *   - a redirect whose TARGET is protected (/profile, the legacy /admin/...
 *     addresses): the route commits at its own public address, but its
 *     <Navigate> effect - earlier in the tree, so it runs first - has already
 *     moved the BROWSER to the target (BrowserRouter writes history at once and
 *     renders the new location in a transition). So the browser's pathname is
 *     checked too, not only the router's.
 *
 * A no-op once the splash is down - every later navigation, every anonymous
 * visitor - with no route matching at all. Never throws (both checks are
 * guarded, the release idempotent), so StrictMode's double effect is harmless.
 */
export function ReleaseAuthBootOnPublicRoute() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!isAuthBootActive()) return;
    if (pathname === "/" || isProtectedPath(pathname)) return;
    if (isProtectedPath(browserPathname())) return;
    releaseAuthBoot();
  }, [pathname]);
  return null;
}

// ---------------------------------------------------------------------------
// preloadAuthBootLanding (backlog #158) - on an authenticated boot, start the
// landing page's chunk NOW, in parallel with GET /auth/session, instead of
// serially after it (the route only renders its lazy page once ProtectedRoute
// stops waiting for the session).
//
// The booted address is the whole signal. A deep link that is not a file on
// the bucket (/dashboard, /settings, ...) gets 404.html, which stores the
// address in sessionStorage and replaces the location with "/". The head
// auth-boot script in public/index.html runs on that "/" - so the splash may be
// up whatever address the visitor is really headed for - but the inline script
// at the end of <body> restores the stored address (history.replaceState) and
// removes the key, and CRA's bundle is a `defer` script in <head>, which runs
// only after the whole document is parsed. So by the time index.js calls this,
// `window.location.pathname` is already the restored address and the key is
// gone: there is nothing left to read from storage.
// ---------------------------------------------------------------------------

/**
 * The addresses whose authenticated boot lands on the Dashboard: "/" (the
 * home-screen start_url; RootRoute sends every hinted visitor to /dashboard,
 * and ProtectedRoute lets admins through it) and /dashboard itself (a deep link
 * or bookmark the body shim restored before index.js ran).
 */
const AUTH_BOOT_DASHBOARD_PATHS = new Set(["/", "/dashboard"]);

/** `path` with its trailing slashes trimmed ("/" stays "/"). */
const trimTrailingSlashes = (path) => (path.length > 1 ? path.replace(/\/+$/, "") || "/" : path);

/**
 * Start the Dashboard chunk when an authenticated boot will land on it - when
 * the booted (already restored) address is "/" or /dashboard. A /settings or
 * /admin bookmark therefore downloads nothing extra: its own route loads its
 * own page. Call only when `window.__LF_AUTH_BOOT__` is set (src/index.js): an
 * anonymous boot must not download the authenticated app.
 *
 * Never throws, and a failed download stays silent here - the route's own lazy
 * render requests the chunk again and surfaces the failure through the normal
 * path (lazyWithPreload retries a rejected import). Only the chunk: the session
 * itself is never prefetched (concurrent refresh rotations can sign the
 * visitor out - docs/homepage-auth-flash-investigation.md).
 *
 * @param {string} pathname the address being booted (`window.location.pathname`,
 *        which the body shim has already restored)
 * @returns {Promise<unknown> | null} the started download - it never rejects
 *          (the module, or undefined on a failure) - or null when this boot
 *          lands elsewhere.
 */
export function preloadAuthBootLanding(pathname) {
  try {
    if (typeof pathname !== "string" || !AUTH_BOOT_DASHBOARD_PATHS.has(trimTrailingSlashes(pathname))) {
      return null;
    }
    return Promise.resolve(Dashboard.preload()).catch(() => undefined);
  } catch (e) {
    /* A preload is an optimisation: the route loads its page either way. */
    return null;
  }
}
